import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  api,
  can,
  type HomeClock,
  type HomeDeadline,
  type HomeJourney,
  type HomeKpi,
  type HomePanel,
  type HomePeriodKey,
  type HomePeriodSummary,
  type Me,
  type MyWorkItem,
} from '../lib/api'
import { ErrorBox } from '../components/DataTable'
import { Skeleton } from '../components/ui'
import { Dialog, Disclosure, Status, formatIN, useCountUp, type Tone } from '../components/kit'
import { StatusChip } from '../components/StatusChip'
import { chipWord, needsSomebody } from '../lib/status'
import { onScreen } from '../lib/screens'
import { docTypeWords } from '../lib/documentTypes'
import { MY_WORK_ALL, countFrom } from '../lib/myWork'
import { useDrawerParam } from '../lib/hashParams'
import { HEALTH_PART, HEALTH_RULE, healthPoints } from '../lib/healthScore'
import {
  BAND_COLOR,
  ClockRing,
  DeadlineStrip,
  Donut,
  JourneyTrack,
  Legend,
  Pipeline,
  Ring,
  Sparkline,
  StackBar,
  TierGrid,
  TooltipProvider,
  WeeklyColumns,
  dayMonthUTC,
  useTooltip,
  type BandMoves,
  type DonutSlice,
} from '../components/charts'
import { SetupGuide } from '../components/SetupGuide'
import { MyWorkList, rowLate, whenWords } from './MyWork'
import '../styles/home-panel.css'

// Home: one page (release 3.3, plan 3.1 of the 7 Oct UX plan).
//
// # WHAT IT IS
//
// Five blocks and a fold. Your actions (the first five rows of My work), a
// summary (the vendors, the reader's figures and the health score), Upcoming
// (what falls due in the period), Risk by tier (a chart or a table) and In
// progress (where each exit, questionnaire and bulk send stands). Under them,
// "More insights", closed on load and remembered per person: what happened in
// the period, the trend of each figure, the findings raised and closed, the
// questionnaire pipeline and the regulatory deadlines. For a workspace that is
// just starting, the setup guide sits above all of it.
//
// # WHY ONE PAGE AND NOT FOUR TABS
//
// Home was 13 blocks and 2,055 px; then four tabs, one part at a time. The
// plan's budget is a page of at most 1.5 windows with at most 5 blocks above
// the fold, and the founder's rule of 13 Oct is that where ours follows the
// plan it wins. So the parts are one page again, each part a block, and what
// a reader needs every morning is above the fold while the rest is one press
// away. The tabs' one safety rule stays: what the fold hides and needs
// somebody (a questionnaire overdue, a purchase blocked, a duty running out)
// is said on its closed line, by the same rules the cards draw with.
//
// # YOUR ACTIONS IS MY WORK
//
// The same rows, in the same order and the same words, as the first five of My
// work (views/MyWork.tsx draws both), and "View all" opens My work. The API
// reads them from the list it assembles for My work and nothing is counted in
// the browser, so the number beside the heading is My work's count. No task is
// drawn twice: a row of In progress that is already a row of Your actions is
// left out of In progress.
//
// # EACH JOB, ITS OWN ORDER
//
// The same five blocks in the order the reader's job leads with: a lead or an
// analyst starts at Your actions, a CISO at Risk by tier, a DPO at In progress;
// a job whose own block holds nothing starts at the summary, as does every
// other job. The order is worked out once, when Home opens, so a refetch never
// moves a block under the pointer. An address that names a part (?part=you,
// risk, journeys or overview, the tabs' old names) scrolls to that block and
// puts the keyboard on it.
//
// # EVERY NUMBER OPENS ITS LIST
//
// Every figure, segment, cell, deadline, clock and row is a link to the list
// it counts, and every count comes from GET /v1/home/panel, which counts by the
// rule of that list. Nothing here is counted in the browser.
//
// # LITTLE DATA
//
// A programme with three unscored vendors is a normal first week. A block with
// nothing to draw says in a plain sentence what it would show ("No findings
// raised or closed in the last 30 days") rather than drawing an empty chart,
// and zeros show as zeros.
//
// # WHAT THE PERIOD SCOPES
//
// The control sits in the header. 7 days, 30 days or Quarter is the window
// before now for what happened, the findings chart, the questionnaires sent
// and the moves between risk bands, and the horizon after now for Upcoming.
// Your actions, In progress, the regulatory deadlines and the risk mix are what
// stands now, and each says "now" so nobody reads them as scoped.
//
// Changing it asks again and holds the previous render at reduced opacity until
// the answer arrives, so nothing flashes a skeleton. Every caption reads the
// period of the answer on screen, not the button just pressed. The period is
// kept in the address (#/home?period=7d), replaced rather than pushed, so Back
// from a list and a reload both come back to the same period.
//
// # WHY?
//
// The health score's "Why?" opens the score's explanation in a drawer (the
// same parts and rules as the programme report's appendix, lib/healthScore.ts),
// kept in the address as ?panel=health so Back closes it and a link opens it.

const PERIODS: { key: HomePeriodKey; label: string; words: string; last: string }[] = [
  { key: '7d', label: '7 days', words: 'in 7 days', last: 'last 7 days' },
  { key: '30d', label: '30 days', words: 'in 30 days', last: 'last 30 days' },
  { key: '90d', label: 'Quarter', words: 'this quarter', last: 'last quarter' },
]
const PERIOD_KEY = 'tprm.home.period'
const RISK_VIEW_KEY = 'tprm.home.riskView'
const STEP_DAYS: Record<HomePeriodKey, number> = { '7d': 1, '30d': 2.5, '90d': 7 }
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function isPeriod(v: string | null | undefined): v is HomePeriodKey {
  return v === '7d' || v === '30d' || v === '90d'
}

function periodInAddress(): HomePeriodKey | null {
  const v = new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('period')
  return isPeriod(v) ? v : null
}

// The address first, so a link, Back or a reload shows the period it names;
// then the last one this browser chose.
function readPeriod(): HomePeriodKey {
  const inAddress = periodInAddress()
  if (inAddress) return inAddress
  try {
    const v = localStorage.getItem(PERIOD_KEY)
    return isPeriod(v) ? v : '30d'
  } catch {
    return '30d'
  }
}

// The part each job leads with (3.3, lane 2). A job whose part is about its
// own work (Your actions, In progress) leads with it only when something is in
// it; otherwise the first part, the summary.
const PERSONA_PART: Record<string, string> = {
  tprm_lead: 'you',
  analyst: 'you',
  business_owner: 'you',
  business_requester: 'you',
  procurement: 'you',
  legal_counsel: 'you',
  approver: 'you',
  ciso: 'risk',
  risk_officer: 'risk',
  security_operator: 'risk',
  dpo: 'journeys',
  compliance_officer: 'journeys',
}
const OWN_WORK = new Set(['you', 'journeys'])

// A part of Home: what ?part= names and what a job leads with, with what is in
// it (count) and what in it needs somebody (alert).
export type HomePart = { key: string; label: string; count?: number; alert?: string }

export function landingPart(personaCode: string | undefined, parts: HomePart[]): string {
  const first = parts[0]?.key ?? ''
  const want = PERSONA_PART[personaCode ?? '']
  const part = parts.find((t) => t.key === want)
  if (!want || !part) return first
  if (OWN_WORK.has(want) && !part.count && !part.alert) return first
  return want
}

// The blocks above the fold, and the order each lead puts them in. The first
// block of an order stands two rows tall beside the next two when it is one of
// the long ones (a list of tasks, the risk chart, the rows in progress).
export type HomeBlock = 'you' | 'summary' | 'upcoming' | 'risk' | 'progress'
const ORDER: Record<string, HomeBlock[]> = {
  you: ['you', 'summary', 'upcoming', 'risk', 'progress'],
  risk: ['risk', 'summary', 'upcoming', 'you', 'progress'],
  journeys: ['progress', 'summary', 'upcoming', 'you', 'risk'],
  overview: ['summary', 'risk', 'upcoming', 'progress', 'you'],
}
const TALL = new Set<HomeBlock>(['you', 'risk', 'progress'])
// The block an address's ?part= names (the four tabs' keys, kept so links work).
const PART_BLOCK: Record<string, HomeBlock> = { overview: 'summary', risk: 'risk', you: 'you', journeys: 'progress' }

// The block an old ?part= link names (a link kept from the four-part Home).
export function blockForPart(part: string | null): HomeBlock | null {
  return PART_BLOCK[part ?? ''] ?? null
}

function blockDrawn(key: HomeBlock, data: HomePanel): boolean {
  switch (key) {
    case 'you':
      return data.needsYou != null
    case 'summary':
      return data.health != null || (data.kpis ?? []).length > 0 || data.vendorCount != null
    case 'upcoming':
      return data.deadlines != null
    case 'risk':
      return data.bands != null && data.tierBands != null
    case 'progress':
      return data.journeys != null
  }
}

// The blocks this reader sees, in their job's order.
export function homeOrder(lead: string, data: HomePanel): HomeBlock[] {
  return (ORDER[lead] ?? ORDER.overview!).filter((b) => blockDrawn(b, data))
}

function partInAddress(): string | null {
  // No window when the panel is drawn on the server (the tests do): no address.
  if (typeof window === 'undefined') return null
  return new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('part')
}

function writePeriod(p: HomePeriodKey) {
  const [path, query] = window.location.hash.replace(/^#/, '').split('?')
  const q = new URLSearchParams(query ?? '')
  q.set('period', p)
  const home = path && path !== '/' ? path : '/home'
  history.replaceState(history.state, '', '#' + home + '?' + q.toString())
}

function lastWords(key: HomePeriodKey): string {
  return PERIODS.find((p) => p.key === key)?.last ?? ''
}

// The time in India, whatever zone the browser is in: the programme runs on
// IST and the header says so.
function istParts(now: Date): { text: string; hour: number } {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    weekday: 'short',
    day: '2-digit',
    month: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now)
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? ''
  const month = MONTHS[Number(get('month')) - 1] ?? ''
  const weekday = DAYS.find((d) => get('weekday').startsWith(d)) ?? get('weekday')
  return {
    text: weekday + ' ' + get('day') + ' ' + month + ' ' + get('year') + ', ' + get('hour') + ':' + get('minute') + ' IST',
    hour: Number(get('hour')),
  }
}

function greeting(hour: number): string {
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

function dayMonth(iso: string | undefined): string {
  if (!iso) return ''
  return dayMonthUTC(new Date(iso.slice(0, 10) + 'T00:00:00Z'))
}

function plural(n: number, one: string, many: string): string {
  return formatIN(n) + ' ' + (n === 1 ? one : many)
}

export function Home({ me }: { me: Me }) {
  const [period, setPeriod] = useState<HomePeriodKey>(readPeriod)
  const [data, setData] = useState<HomePanel | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<unknown>(null)
  // Back, or a link to another period, while Home is already showing.
  useEffect(() => {
    const sync = () => {
      const p = periodInAddress()
      if (p) setPeriod(p)
    }
    window.addEventListener('hashchange', sync)
    return () => window.removeEventListener('hashchange', sync)
  }, [])

  // Home opened without a period in the address gets the one it is showing,
  // so the address always says what the screen counts.
  useEffect(() => {
    if (periodInAddress() !== period) writePeriod(period)
  }, [period])

  useEffect(() => {
    let live = true
    setLoading(true)
    const pending = api.homePanel(period)
    // The panel carries My work's count; the sidebar and the bell take it from
    // here rather than asking the API to assemble the list a second time.
    countFrom(pending.then((p) => p.myWork))
    pending
      .then((p) => {
        if (!live) return
        setData(p)
        setError(null)
      })
      .catch((err) => {
        if (live) setError(err)
      })
      .finally(() => {
        if (live) setLoading(false)
      })
    return () => {
      live = false
    }
  }, [period, me.subject])

  const choose = useCallback((p: HomePeriodKey) => {
    setPeriod(p)
    writePeriod(p)
    try {
      localStorage.setItem(PERIOD_KEY, p)
    } catch {
      // A browser that refuses storage keeps the choice for this visit.
    }
  }, [])

  if (error != null && !data) return <ErrorBox error={error} what="Home" />

  return (
    <TooltipProvider>
      <div className={'hp hc' + (loading && data ? ' refetching' : '')} aria-busy={loading}>
        <Header me={me} data={data} period={period} onPeriod={choose} />
        {!data ? <Loading /> : <Panel me={me} data={data} period={data.period?.key ?? period} />}
      </div>
    </TooltipProvider>
  )
}

function Header({
  me,
  data,
  period,
  onPeriod,
}: {
  me: Me
  data: HomePanel | null
  period: HomePeriodKey
  onPeriod: (p: HomePeriodKey) => void
}) {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const t = window.setInterval(() => setNow(new Date()), 30_000)
    return () => window.clearInterval(t)
  }, [])
  const ist = istParts(now)
  const personaName = data?.persona?.name ?? me.persona?.name
  const report = can('reports.read') && onScreen('reports')

  return (
    <div className="hp-head">
      <div className="grow">
        <h1>{greeting(ist.hour)}</h1>
        <div className="hp-sub">
          {personaName && <span>{personaName}</span>}
          <span className="hp-mono">{ist.text}</span>
        </div>
      </div>
      <span className="hp-seg" role="group" aria-label="Period">
        {PERIODS.map((p) => (
          <button key={p.key} type="button" aria-pressed={period === p.key} onClick={() => onPeriod(p.key)}>
            {p.label}
          </button>
        ))}
      </span>
      {report && (
        <a className="hp-btn brand" href={'#/report/programme?period=' + period}>
          Generate report
        </a>
      )}
    </div>
  )
}

function Loading() {
  return (
    <div className="hp-board">
      <section className="hp-card hp-b tall">
        <Skeleton lines={8} />
      </section>
      <section className="hp-card hp-b">
        <Skeleton lines={3} />
      </section>
      <section className="hp-card hp-b">
        <Skeleton lines={4} />
      </section>
    </div>
  )
}

// An address that names a part scrolls to its block and puts the keyboard on
// it, when Home opens and when a link to another part arrives while it shows.
// The same part twice is not acted on again, so Back closing the drawer does
// not move the page.
function useAskedPart() {
  const last = useRef<string | null>(null)
  useEffect(() => {
    const go = () => {
      const part = partInAddress()
      if (part === last.current) return
      last.current = part
      const block = blockForPart(part)
      const el = block ? document.getElementById('hp-b-' + block) : null
      if (!el) return
      el.scrollIntoView({ block: 'start' })
      el.focus({ preventScroll: true })
    }
    const t = window.setTimeout(go, 0)
    window.addEventListener('hashchange', go)
    return () => {
      window.clearTimeout(t)
      window.removeEventListener('hashchange', go)
    }
  }, [])
}

export function Panel({ me, data, period }: { me: Me; data: HomePanel; period: HomePeriodKey }) {
  const words = PERIODS.find((p) => p.key === period)?.words ?? ''
  // The order is worked out once, when Home opens: a refetch that empties a
  // block must not move the blocks under the reader.
  const [lead] = useState(() => landingPart(me.persona?.code, leadParts(data)))
  const order = homeOrder(lead || 'overview', data)
  const why = useDrawerParam('panel')
  useAskedPart()
  const mark = insightsMark(data)
  const tasks = data.needsYou?.items ?? []

  const block = (key: HomeBlock, tall: boolean) => {
    const cls = 'hp-card hp-b' + (tall ? ' tall' : '')
    switch (key) {
      case 'you':
        return <YourActions key={key} needs={data.needsYou!} cls={cls} />
      case 'summary':
        return <Summary key={key} data={data} words={words} cls={cls} onWhy={() => why.open('health')} />
      case 'upcoming':
        return <Upcoming key={key} data={data} cls={cls} />
      case 'risk':
        return <RiskByTier key={key} data={data} period={period} cls={cls} />
      case 'progress':
        return <InProgress key={key} journeys={data.journeys!} tasks={tasks} cls={cls} />
    }
  }

  return (
    <>
      <SetupGuide me={me} vendorCount={data.vendorCount} />

      <div className="hp-board">{order.map((key, i) => block(key, i === 0 && TALL.has(key) && order.length > 2))}</div>

      <Disclosure
        title="More insights"
        summary={mark ? <Status tone="warn">{mark}</Status> : insightsKinds(data)}
        remember={'home.insights.' + me.subject}
      >
        <Insights data={data} period={period} words={words} />
      </Disclosure>

      {data.health && (
        <Dialog shape="drawer" kind="record" open={why.value === 'health'} onClose={() => void why.close()} title="Why this health score">
          <HealthWhy health={data.health} words={words} />
        </Dialog>
      )}
    </>
  )
}

// ------------------------------------------------------------ the marks

// The date in India a moment falls on, as YYYY-MM-DD.
function istDay(iso: string): string {
  const t = new Date(iso).getTime()
  return Number.isNaN(t) ? iso.slice(0, 10) : new Date(t + 330 * 60_000).toISOString().slice(0, 10)
}

// Whether a deadline falls today in India or has passed. The landing and the
// Upcoming list's "Today" both ask this, so they cannot disagree in the five
// and a half hours when the date in India is a day ahead of the date in UTC.
function dueByToday(d: HomeDeadline, computedAt: string): boolean {
  return d.date.slice(0, 10) <= istDay(computedAt)
}

// How pressing a running duty is. The ring's colour and the fold's mark both
// come from here: a clock drawn amber or red is never behind a fold that says
// nothing (review of 11 Oct 2026).
function clockState(c: HomeClock): 'owed' | 'running-out' | 'in-time' {
  if (c.overdue || c.withoutDelay) return 'owed'
  return c.elapsed >= 0.5 ? 'running-out' : 'in-time'
}

function said(...parts: (string | false)[]): { alert?: string } {
  const words = parts.filter(Boolean).join(', ')
  return words ? { alert: words } : {}
}

// "2 journeys blocked or overdue": how many rows need somebody, in the words
// their own chips show.
function inTheirWords(words: string[], one: string, many: string): string | false {
  if (words.length === 0) return false
  return plural(words.length, one, many) + ' ' + [...new Set(words)].join(' or ')
}

// What the questionnaires card draws as needing somebody.
function questionnaireMarks(data: HomePanel): (string | false)[] {
  const late = data.pipeline?.overdue ?? 0
  const blocked = data.pipeline ? (data.clearance?.blocked ?? 0) : 0
  return [late > 0 && plural(late, 'questionnaire', 'questionnaires') + ' overdue', blocked > 0 && plural(blocked, 'purchase', 'purchases') + ' blocked']
}

// What the regulatory deadlines card draws amber or red.
function clockMarks(clocks: HomeClock[]): (string | false)[] {
  const owed = clocks.filter((c) => clockState(c) === 'owed').length
  const short = clocks.filter((c) => clockState(c) === 'running-out').length
  return [
    owed > 0 && plural(owed, 'duty', 'duties') + ' overdue or owed now',
    short > 0 && plural(short, 'duty', 'duties') + (short === 1 ? ' past half its time' : ' past half their time'),
  ]
}

// What "More insights" holds for this reader, said on its closed line when
// nothing in it needs somebody: only the kinds their panel carries (a job
// without the questionnaires or the duties is not promised them).
export function insightsKinds(data: HomePanel): string {
  const kinds = [
    ((data.kpis ?? []).length > 0 || data.periodSummary) && 'trends',
    (data.findingsTrend || data.findingsWeekly) && 'findings',
    data.pipeline && 'questionnaires',
    data.clocks && 'regulatory deadlines',
  ].filter((k): k is string => typeof k === 'string')
  if (kinds.length === 0) return 'Nothing more for you here'
  const words = kinds.join(', ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

// What "More insights" holds that needs somebody, said on its closed line.
// The fold is closed on load, so nothing in it may be late, blocked or running
// out without the line saying so; each mark asks the rule its card draws with.
export function insightsMark(data: HomePanel): string {
  return said(...questionnaireMarks(data), ...clockMarks(data.clocks ?? [])).alert ?? ''
}

// The parts of Home, as ?part= names them and as each job leads with them,
// with what is in each and what in it needs somebody. Each mark asks the rule
// the card itself draws with (the status chip's word, My work's lateness, the
// clock's ring), so the landing cannot pass over a part with something wrong in
// it: three reviews each found one more row the marks had a narrower idea of.
// The marks cover the rows the cards draw; what is past those rows is reached
// by the card's own "more" link.
// What each lead's own block holds, for choosing the lead: Your actions holds
// the tasks only (the deadlines are in Upcoming) and In progress the rows only
// (the duties running are under More insights). A lead with no task and one
// deadline today is not led by an empty "Nothing waiting for you".
export function leadParts(data: HomePanel): HomePart[] {
  return homeParts({ ...data, deadlines: undefined, clocks: undefined })
}

export function homeParts(data: HomePanel): HomePart[] {
  const parts: HomePart[] = []
  if (data.health != null || (data.kpis ?? []).length > 0)
    parts.push({
      key: 'overview',
      label: 'Overview',
      ...said(data.health != null && data.health.score < 50 && 'health score ' + data.health.score + ', critical'),
    })
  if (data.bands || data.findingsWeekly || data.findingsTrend || data.pipeline) {
    parts.push({ key: 'risk', label: 'Risk and findings', ...said(...questionnaireMarks(data)) })
  }
  if (data.needsYou || data.deadlines) {
    const tasks = (data.needsYou?.items ?? []).flatMap((it) => {
      if (whenWords(it).late) return ['overdue']
      const chip = chipWord(it.status.domain, it.status.code, rowLate(it))
      return needsSomebody(chip) ? [chip.word.toLowerCase()] : []
    })
    const deadlines = data.deadlines ?? []
    const due = deadlines.filter((d) => dueByToday(d, data.computedAt)).length
    // Drawn critical by its own chip though its date has not come.
    const critical = deadlines.filter((d) => !dueByToday(d, data.computedAt) && d.tone === 'crit').length
    parts.push({
      key: 'you',
      label: 'Your actions',
      ...(data.needsYou ? { count: data.needsYou.total } : {}),
      ...said(
        inTheirWords(tasks, 'task', 'tasks'),
        due > 0 && plural(due, 'deadline', 'deadlines') + ' due today or earlier',
        critical > 0 && plural(critical, 'deadline', 'deadlines') + ' marked critical',
      ),
    })
  }
  if (data.journeys || data.clocks) {
    const clocks = data.clocks ?? []
    const journeys = (data.journeys?.items ?? []).flatMap((j) => {
      const chip = chipWord(j.status.domain, j.status.code, j.late)
      if (needsSomebody(chip)) return [chip.word.toLowerCase()]
      // The track under the row draws a blocked or rejected stage in red even
      // where the row's own status does not say so.
      const stuck = (j.track ?? []).find((t) => t === 'blocked' || t === 'rejected')
      return stuck ? [stuck] : []
    })
    // What is in progress and the duties running.
    const held = (data.journeys?.total ?? 0) + clocks.length
    parts.push({
      key: 'journeys',
      label: 'In progress and deadlines',
      ...(held > 0 ? { count: held } : {}),
      ...said(...clockMarks(clocks), inTheirWords(journeys, 'item', 'items')),
    })
  }
  return parts
}

// A quiet caption for a block the period does not scope.
function NowCaption() {
  return (
    <small className="hp-now" title="As it stands now. The period does not change this.">
      now
    </small>
  )
}

// ---------------------------------------------------------- your actions

// The first rows of My work, drawn by My work's own list so a row has the same
// words and the same one button here as there. View all opens the whole list.
function YourActions({ needs, cls }: { needs: NonNullable<HomePanel['needsYou']>; cls: string }) {
  // What was done from a row on this visit. The row stays, quietly, so the
  // list does not jump under the pointer; the next read leaves it out.
  const [done, setDone] = useState<Record<string, string>>({})
  return (
    <section id="hp-b-you" className={cls + ' hp-needs'} aria-labelledby="hp-h-queue" tabIndex={-1}>
      <div className="hp-ch">
        <h2 id="hp-h-queue" className="grow">
          Your actions <span className="hp-count">{formatIN(needs.total)}</span> <NowCaption />
        </h2>
        {needs.total > 0 && (
          <a className="hp-link" href={MY_WORK_ALL}>
            View all
          </a>
        )}
      </div>
      {needs.items.length === 0 ? (
        <div className="hp-empty">
          <strong>Nothing waiting for you</strong>
        </div>
      ) : (
        <MyWorkList items={needs.items} done={done} onDone={(key, what) => setDone((d) => ({ ...d, [key]: what }))} />
      )}
      {needs.total > needs.items.length && (
        <p className="hp-more">
          {formatIN(needs.total - needs.items.length)} more in <a href={MY_WORK_ALL}>My work</a>
        </p>
      )}
    </section>
  )
}

// --------------------------------------------------------------- summary

function signed(n: number): string {
  return (n > 0 ? '+' : n < 0 ? '-' : '') + formatIN(Math.abs(n))
}

function changeWords(k: HomeKpi): { text: string; cls: string } {
  if (k.change == null) return { text: k.seriesSince ? 'from ' + dayMonth(k.seriesSince) : '', cls: 'flat' }
  if (k.change === 0) return { text: 'no change', cls: 'flat' }
  const text = signed(k.change)
  if (k.direction === 'neutral') return { text, cls: 'flat' }
  const bad = (k.direction === 'up_bad') === k.change > 0
  return { text, cls: bad ? 'up-bad' : 'down-good' }
}

// The vendors, the reader's own figures (the API chooses them by job) and the
// health score, as one compact block. Each figure opens the list it counts;
// the trend of each is in More insights.
function Summary({ data, words, cls, onWhy }: { data: HomePanel; words: string; cls: string; onWhy: () => void }) {
  const kpis = data.kpis ?? []
  const register = onScreen('vendors')
  return (
    <section id="hp-b-summary" className={cls + ' hp-summary'} aria-labelledby="hp-h-sum" tabIndex={-1}>
      <h2 id="hp-h-sum" className="vh">
        Summary
      </h2>
      {(data.vendorCount != null || kpis.length > 0) && (
        <div className="hp-figs">
          {data.vendorCount != null && <VendorFigure count={data.vendorCount} href={register ? '#/vendors' : undefined} />}
          {kpis.map((k) => (
            <KpiFigure key={k.key} kpi={k} words={words} />
          ))}
        </div>
      )}
      {data.health && <HealthLine health={data.health} words={words} onWhy={onWhy} />}
    </section>
  )
}

// The count rolls up on screen, so the drawn figure is hidden from a screen
// reader and the count is said once, whole. A span has no role an aria-label
// is read on, so where the figure is not a link the count is text (vh) rather
// than a label nobody hears (lane 2 leftovers, 13 Oct 2026).
function VendorFigure({ count, href }: { count: number; href?: string }) {
  const shown = useCountUp(count, 800)
  const inner = (
    <>
      <span className="l">Vendors</span>
      <span className="v" aria-hidden="true">
        <span>{formatIN(shown)}</span>
      </span>
      <span className="vh">{': ' + formatIN(count)}</span>
    </>
  )
  return href ? (
    <a className="hp-fig" href={href} aria-label={'Vendors: ' + count}>
      {inner}
    </a>
  ) : (
    <span className="hp-fig">{inner}</span>
  )
}

function KpiFigure({ kpi, words }: { kpi: HomeKpi; words: string }) {
  const shown = useCountUp(kpi.value, 800)
  const ch = changeWords(kpi)
  const hot = kpi.value > 0 && kpi.direction === 'up_bad'
  const aria = kpi.label + ': ' + kpi.value + (kpi.change == null ? '' : ', ' + (kpi.change === 0 ? 'no change' : signed(kpi.change)) + ' ' + words)
  return (
    <a className={'hp-kpi hp-fig' + (hot ? ' hot' : '')} href={kpi.href} aria-label={aria}>
      <span className="l">{kpi.label}</span>
      <span className="v" aria-hidden="true">
        <span>{formatIN(shown)}</span>
        {kpi.change != null && kpi.change !== 0 && <span className={'d ' + ch.cls}>{ch.text}</span>}
      </span>
    </a>
  )
}

function HealthLine({ health, words, onWhy }: { health: NonNullable<HomePanel['health']>; words: string; onWhy: () => void }) {
  const tone: Tone = health.score >= 75 ? 'ok' : health.score >= 50 ? 'warn' : 'crit'
  const shown = useCountUp(health.score, 800)
  return (
    <div className="hp-hline">
      <span className="l">Health score</span>
      {/* The number counts up; a screen reader is given the score itself. */}
      <b className="hp-hscore">
        <span aria-hidden="true">{formatIN(shown)}</span>
        <span className="vh">{health.score} of 100</span>
      </b>
      <Status tone={tone}>{health.word}</Status>
      {health.change != null && health.change !== 0 && (
        <span className={'d ' + (health.change > 0 ? 'down-good' : 'up-bad')}>
          {signed(health.change)} {words}
        </span>
      )}
      <button type="button" className="hp-link" aria-haspopup="dialog" onClick={onWhy}>
        Why?
      </button>
    </div>
  )
}

// The score's explanation: the ring, then each part with its rule, what it
// counts now and the points it costs, each opening the list behind it. The
// same parts and rules as the programme report's appendix.
export function HealthWhy({ health, words }: { health: NonNullable<HomePanel['health']>; words: string }) {
  const tone: Tone = health.score >= 75 ? 'ok' : health.score >= 50 ? 'warn' : 'crit'
  const parts = [...health.components].sort((a, b) => b.lost - a.lost)
  return (
    <div className="hp-why">
      <div className="hp-why-head">
        <Ring score={health.score} word={health.word} />
        <div>
          <Status tone={tone}>{health.word}</Status>
          <p>
            100 less the points lost.{' '}
            {health.change == null ? <>Counted since {dayMonth(health.seriesSince)}.</> : health.change === 0 ? <>No change {words}.</> : <>{signed(health.change)} {words}.</>}
          </p>
        </div>
      </div>
      <table className="hp-why-table">
        <thead>
          <tr>
            <th scope="col">Part</th>
            <th scope="col">Now</th>
            <th scope="col" className="n">
              Points lost
            </th>
          </tr>
        </thead>
        <tbody>
          {parts.map((c) => (
            <tr key={c.key}>
              <td>
                <b>{HEALTH_PART[c.key] ?? c.label}</b>
                <span>{HEALTH_RULE[c.key] ?? ''}</span>
              </td>
              <td>{c.href && c.lost > 0 ? <a href={c.href}>{c.label}</a> : c.label}</td>
              <td className="n">
                <span className="hp-lost" aria-hidden="true">
                  <i style={{ width: (c.max > 0 ? Math.min(1, c.lost / c.max) : 0) * 100 + '%' }} />
                </span>
                {healthPoints(c.lost)} of {c.max}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// -------------------------------------------------------------- upcoming

// A deadline title as words: the API writes some with the stored document
// kind ("Contract ends: MSA"), which is a code, not a word.
// The API's titles in the glossary's words where they differ: it still names a
// risk acceptance an accepted risk (packages/core/repo/home_parts.go).
const DEADLINE_WORDS: Record<string, string> = { 'Accepted risk expires': 'Risk acceptance expires' }

export function deadlineTitle(title: string): string {
  const m = /^(Contract ends|Certificate expires): ([A-Z0-9_]+)$/.exec(title)
  return m ? m[1] + ': ' + docTypeWords(m[2] ?? '') : (DEADLINE_WORDS[title] ?? title)
}

function Upcoming({ data, cls }: { data: HomePanel; cls: string }) {
  const items = data.deadlines ?? []
  // The strip reads its day off the UTC fields of this date, so it is handed
  // the day in India carried that way: the day the list under it calls "Today"
  // (dueByToday). Handed the instant, it drew yesterday as today from midnight
  // to 05:30 in India, a day behind the list.
  const today = new Date(istDay(data.computedAt) + 'T00:00:00Z')
  // Three rows: the strip above draws every one of them, and the page stays
  // inside its budget at 1024 x 768.
  const shown = items.slice(0, 3)
  // The horizon is the period's length, as the API counted it.
  const days = data.period?.days ?? 30
  return (
    <section id="hp-b-upcoming" className={cls} aria-labelledby="hp-h-dead" tabIndex={-1}>
      <div className="hp-ch">
        <h2 id="hp-h-dead" className="grow">
          Upcoming <span className="hp-count">{formatIN(items.length)}</span>
        </h2>
        <small>next {days} days</small>
      </div>
      {items.length === 0 ? (
        <p className="hp-empty">Nothing falls due in the next {days} days.</p>
      ) : (
        <>
          <DeadlineStrip items={items} today={today} span={days} />
          <ul className="hp-dlist">
            {shown.map((d, i) => {
              const when = dueByToday(d, data.computedAt) ? 'Today' : dayMonth(d.date)
              return (
                <li key={i}>
                  <a href={d.href}>
                    <span className="hp-due">{when}</span>
                    <span className="hp-what">
                      <b title={deadlineTitle(d.title)}>{deadlineTitle(d.title)}</b>
                      <span title={d.subject}>{d.subject}</span>
                    </span>
                    <span>
                      <Status tone={d.tone as Tone}>{d.word}</Status>
                    </span>
                  </a>
                </li>
              )
            })}
          </ul>
        </>
      )}
      {items.length > shown.length && <p className="hp-more">{formatIN(items.length - shown.length)} more on the strip above</p>}
    </section>
  )
}

// ------------------------------------------------------------------ risk

function readRiskView(): 'chart' | 'table' {
  try {
    return localStorage.getItem(RISK_VIEW_KEY) === 'table' ? 'table' : 'chart'
  } catch {
    return 'chart'
  }
}

// The risk mix as a chart (the ring of bands and its legend) or as a table
// (tier by band), one at a time. Both stay mounted; the one not chosen is
// hidden by its wrapper, which carries no display rule of its own.
function RiskByTier({ data, period, cls }: { data: HomePanel; period: HomePeriodKey; cls: string }) {
  const [view, setView] = useState<'chart' | 'table'>(readRiskView)
  const pick = (v: 'chart' | 'table') => {
    setView(v)
    try {
      localStorage.setItem(RISK_VIEW_KEY, v)
    } catch {
      // Kept for this visit only.
    }
  }
  const bands = data.bands ?? []
  const moves: BandMoves | undefined = data.bandMoves
    ? Object.fromEntries(data.bandMoves.map((m) => [m.band, { in: m.in, out: m.out }]))
    : undefined
  const grid = data.tierBands!
  const slices: DonutSlice[] = bands.map((b) => ({
    key: b.key,
    label: b.label,
    count: b.count,
    color: BAND_COLOR[b.key] ?? 'var(--hc-b-none)',
    href: b.href || undefined,
  }))
  const total = bands.reduce((a, b) => a + b.count, 0)
  return (
    <section id="hp-b-risk" className={cls} aria-labelledby="hp-h-risk" tabIndex={-1}>
      <div className="hp-ch">
        <h2 id="hp-h-risk" className="grow">
          Risk by tier <NowCaption />
        </h2>
        {total > 0 && (
          <span className="hp-seg sm" role="group" aria-label="Show the risk as">
            <button type="button" aria-pressed={view === 'chart'} onClick={() => pick('chart')}>
              Chart
            </button>
            <button type="button" aria-pressed={view === 'table'} onClick={() => pick('table')}>
              Table
            </button>
          </span>
        )}
      </div>
      {total === 0 ? (
        <p className="hp-empty">No vendors are on the register yet. The risk mix appears here once the first one is added.</p>
      ) : (
        <>
          <div className="hp-view" hidden={view !== 'chart'}>
            <div className="hc-donutrow">
              <Donut slices={slices} total={total} unit="vendors" title="Risk band" />
              <Legend slices={slices} moves={moves} movesWords={'over the ' + lastWords(period)} />
            </div>
          </div>
          <div className="hp-view" hidden={view !== 'table'}>
            <div className="hp-heat-wrap">
              <TierGrid bands={grid.bands} short={grid.short} rows={grid.rows} />
            </div>
          </div>
        </>
      )}
    </section>
  )
}

// ----------------------------------------------------------- in progress

// Whether a row in progress is the same thing as a task in Your actions: a
// questionnaire by its assessment, an exit by its vendor, a bulk send by its
// address. Such a row is left out, so no task is on Home twice.
function sameAsTask(j: HomeJourney, t: MyWorkItem): boolean {
  if (j.kind === 'questionnaire' && t.assessmentId) return j.key === 'a' + t.assessmentId
  if (j.kind === 'exit' && t.vendorId && t.kind.startsWith('exit')) return j.key === 'x' + t.vendorId
  return t.action.href === j.href
}

export function progressRows(journeys: HomeJourney[], tasks: MyWorkItem[]): HomeJourney[] {
  return journeys.filter((j) => !tasks.some((t) => sameAsTask(j, t))).slice(0, 3)
}

function InProgress({ journeys, tasks, cls }: { journeys: NonNullable<HomePanel['journeys']>; tasks: MyWorkItem[]; cls: string }) {
  const rows = progressRows(journeys.items, tasks)
  return (
    <section id="hp-b-progress" className={cls} aria-labelledby="hp-h-j" tabIndex={-1}>
      <div className="hp-ch">
        <h2 id="hp-h-j" className="grow">
          In progress <span className="hp-count">{formatIN(journeys.total)}</span> <NowCaption />
        </h2>
      </div>
      {rows.length === 0 ? (
        <p className="hp-empty">{journeys.items.length === 0 ? 'Nothing in progress.' : 'Each one is in Your actions.'}</p>
      ) : (
        <div className="hp-jl">
          {rows.map((x) => (
            <a key={x.key} className="hp-jr" href={x.href} aria-label={x.title + (x.context ? ', ' + x.context : '')}>
              <span className="n" title={x.title}>
                {x.title}
              </span>
              <span className="s" title={x.context}>
                {x.context}
              </span>
              <StatusChip domain={x.status.domain} code={x.status.code} late={x.late} />
              {x.track && x.track.length > 0 ? <JourneyTrack states={x.track} label={x.trackLabel ?? x.title} /> : <span />}
            </a>
          ))}
        </div>
      )}
      {journeys.total > rows.length && (
        <p className="hp-more">
          {formatIN(journeys.total - rows.length)} more in {onScreen('exits') ? <a href="#/exits">Offboarding</a> : 'Offboarding'} and{' '}
          {onScreen('assessments') ? <a href="#/assessments">Assessments</a> : 'Assessments'}
        </p>
      )}
    </section>
  )
}

// ---------------------------------------------------------- more insights

function Insights({ data, period, words }: { data: HomePanel; period: HomePeriodKey; words: string }) {
  const kpis = data.kpis ?? []
  return (
    <div className="hp-insights">
      {data.periodSummary && <PeriodStrip summary={data.periodSummary} period={period} />}
      {kpis.length > 0 && <Trends kpis={kpis} words={words} computedAt={data.computedAt} period={period} />}
      {(data.findingsTrend || data.findingsWeekly || data.pipeline || data.clocks) && (
        <div className="hp-grid">
          {(data.findingsTrend || data.findingsWeekly) && <FindingsCard data={data} period={period} />}
          {data.pipeline && <QuestionnaireCard data={data} period={period} />}
          {data.clocks && <ClocksCard clocks={data.clocks} />}
        </div>
      )}
    </div>
  )
}

// What happened in the period, as one strip: the window in words, then a chip
// per count opening its list. A count whose list is another job's is shown
// and is not a link. Zero is shown as 0.
function PeriodStrip({ summary, period }: { summary: HomePeriodSummary; period: HomePeriodKey }) {
  const last = lastWords(period)
  const lead = last.charAt(0).toUpperCase() + last.slice(1)
  return (
    <div className="hp-period" role="group" aria-label={'What happened in the ' + last}>
      <p className="hp-period-h">
        <b>{lead}</b>, since {dayMonth(summary.since)}
      </p>
      <ul className="hp-chips">
        {summary.items.map((it) => (
          <li key={it.key}>
            <PeriodChip label={it.label} count={it.count} href={it.href} />
          </li>
        ))}
      </ul>
    </div>
  )
}

function PeriodChip({ label, count, href }: { label: string; count: number; href: string }) {
  const shown = useCountUp(count, 700)
  const inner = (
    <>
      <span className="l">{label}</span>
      <b className={'n' + (count === 0 ? ' zero' : '')}>{formatIN(shown)}</b>
    </>
  )
  return href ? (
    <a className="hp-chip" href={href} aria-label={label + ': ' + count}>
      {inner}
    </a>
  ) : (
    <span className="hp-chip" aria-label={label + ': ' + count}>
      {inner}
    </span>
  )
}

// The trend of each of the summary's figures over the period: its line and
// its change. The figure itself is in the summary, once.
function Trends({ kpis, words, computedAt, period }: { kpis: HomeKpi[]; words: string; computedAt: string; period: HomePeriodKey }) {
  return (
    <div className="hp-trends" role="group" aria-label={'Trend of each figure, ' + lastWords(period)}>
      {kpis.map((k) => (
        <Trend key={k.key} kpi={k} words={words} computedAt={computedAt} period={period} />
      ))}
    </div>
  )
}

function Trend({ kpi, words, computedAt, period }: { kpi: HomeKpi; words: string; computedAt: string; period: HomePeriodKey }) {
  const ch = changeWords(kpi)
  const labels = useMemo(() => {
    const at = new Date(computedAt).getTime()
    return kpi.series.map((_, i) => dayMonthUTC(new Date(at - (kpi.series.length - 1 - i) * STEP_DAYS[period] * 86400000)))
  }, [computedAt, kpi.series, period])
  return (
    <a className="hp-trend" href={kpi.href} aria-label={kpi.label + (ch.text ? ', ' + ch.text + (kpi.change == null ? '' : ' ' + words) : '')}>
      <span className="l">{kpi.label}</span>
      {ch.text && <span className={'d ' + ch.cls}>{ch.text}</span>}
      <Sparkline series={kpi.series} labels={labels} />
    </a>
  )
}

function FindingsCard({ data, period }: { data: HomePanel; period: HomePeriodKey }) {
  // The trend over the period where the API sends one; twelve weeks from an
  // API that predates it.
  const trend = data.findingsTrend
  const weekly = data.findingsWeekly
  const buckets = trend ? trend.buckets : (weekly?.weeks ?? [])
  const stillOpen = trend ? trend.stillOpen : (weekly?.stillOpen ?? 0)
  const href = (trend ? trend.href : weekly?.href) || undefined
  const unit = trend ? trend.unit : 'week'
  const caption = trend ? lastWords(period) : '12 weeks'
  // Nothing raised or closed in the window draws no chart: a row of empty
  // columns says less than a sentence.
  const quiet = buckets.every((w) => w.raised === 0 && w.closed === 0)
  return (
    <section className="hp-card c5" aria-labelledby="hp-h-trend">
      <div className="hp-ch">
        <h2 id="hp-h-trend" className="grow">
          Findings raised and closed
        </h2>
        <small>{caption}</small>
      </div>
      <div className="hc-keyrow">
        {!quiet && (
          <>
            <span>
              <i style={{ background: 'var(--hc-s1)' }} />
              Raised
            </span>
            <span>
              <i style={{ background: 'var(--hc-s2)' }} />
              Closed
            </span>
          </>
        )}
        <span style={{ marginLeft: 'auto', color: 'var(--ink)' }}>
          {href ? (
            <a href={href} style={{ color: 'inherit', textDecoration: 'none' }}>
              <b className="hc-num">{formatIN(stillOpen)}</b> still open
            </a>
          ) : (
            <>
              <b className="hc-num">{formatIN(stillOpen)}</b> still open
            </>
          )}
        </span>
      </div>
      {quiet ? (
        <p className="hp-empty">No findings raised or closed in the {caption}</p>
      ) : (
        /* Keyed by the period, so a new answer grows its bars from the axis on
           the new scale rather than swapping bars in place. */
        <WeeklyColumns
          key={trend ? period : 'weeks'}
          weeks={buckets.map((w) => ({ label: w.label, a: w.raised, b: w.closed, open: w.open }))}
          names={['Raised', 'Closed']}
          colors={['var(--hc-s1)', 'var(--hc-s2)']}
          openLabel="Still open"
          href={href}
          unit={unit}
          title={'Findings raised and closed per ' + unit + ', ' + caption}
        />
      )}
    </section>
  )
}

function QuestionnaireCard({ data, period }: { data: HomePanel; period: HomePeriodKey }) {
  const p = data.pipeline!
  const c = data.clearance
  const scoped = p.scope === 'period'
  return (
    <section className="hp-card c3" aria-labelledby="hp-h-funnel">
      <div className="hp-ch">
        <h2 id="hp-h-funnel" className="grow">
          Questionnaires
        </h2>
        <small>{scoped ? 'sent in the ' + lastWords(period) : 'all of them'}</small>
      </div>
      {p.steps.every((s) => s.count === 0) ? (
        <p className="hp-empty">{scoped ? 'No questionnaires sent in the ' + lastWords(period) : 'No questionnaires have been sent yet'}</p>
      ) : (
        <Pipeline steps={p.steps.map((s) => ({ ...s, href: s.href || undefined }))} title="Questionnaires" />
      )}
      <div className="hp-fnote">
        {p.overdue > 0 ? (
          <a href={p.overdueHref || undefined} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            {/* Late is amber, as the status table has it: red is for the critical. */}
            <Status tone="warn">{formatIN(p.overdue)} overdue now</Status>
            {p.overdueVendor && (
              <span>
                {p.overdueVendor}, {plural(p.overdueDays ?? 0, 'day', 'days')} late
              </span>
            )}
          </a>
        ) : (
          <>
            <Status tone="ok">None overdue now</Status>
          </>
        )}
      </div>
      {c && (
        <div className="hp-clear">
          <h3>Purchase clearance in {c.system}</h3>
          <StackBar
            title={c.system + ' purchase requests'}
            parts={[
              { key: 'CLEARED', label: 'Cleared', count: c.cleared, color: 'var(--ok-solid)', href: c.href ? c.href + '?status=CLEARED' : undefined },
              {
                key: 'PENDING_ASSESSMENT',
                label: 'Pending',
                count: c.pending,
                color: 'var(--warn-solid)',
                href: c.href ? c.href + '?status=PENDING_ASSESSMENT' : undefined,
              },
              { key: 'BLOCKED', label: 'Blocked', count: c.blocked, color: 'var(--crit-solid)', href: c.href ? c.href + '?status=BLOCKED' : undefined },
            ]}
          />
        </div>
      )}
    </section>
  )
}

function clockColor(c: HomeClock): string {
  const state = clockState(c)
  return state === 'owed' ? 'var(--crit-solid)' : state === 'running-out' ? 'var(--warn-solid)' : 'var(--info-solid)'
}

function ClocksCard({ clocks }: { clocks: HomeClock[] }) {
  const tip = useTooltip()
  const info = 'Time left on reporting duties for open vendor incidents, and accepted risks expired or expiring within a week'
  const shown = clocks.slice(0, 6)
  return (
    <section className="hp-card c4" aria-labelledby="hp-h-clk">
      <div className="hp-ch">
        <h2 id="hp-h-clk" className="grow">
          Regulatory deadlines <NowCaption />
        </h2>
        <span
          className="hp-info"
          tabIndex={0}
          role="note"
          aria-label={info}
          title={info}
          onPointerMove={(e) => tip.show(e, 'Regulatory deadlines', [{ label: 'Incident duties and acceptances', value: String(clocks.length) }])}
          onPointerLeave={() => tip.hide()}
        >
          i
        </span>
      </div>
      {shown.length === 0 ? (
        <p className="hp-empty">None running</p>
      ) : (
        <div>
          {shown.map((c, i) => (
            <a key={i} className="hp-clock" href={c.href} aria-label={c.title + ', ' + c.subject + ', ' + c.words}>
              <ClockRing elapsed={c.elapsed} color={clockColor(c)} />
              <div>
                <b>{c.title}</b>
                <span>
                  {c.subject}. {c.words}
                </span>
              </div>
            </a>
          ))}
        </div>
      )}
      {clocks.length > shown.length && <p className="hp-more">{formatIN(clocks.length - shown.length)} more running</p>}
    </section>
  )
}
